// @flow
import * as React from 'react'
import get from 'lodash/get'
import moment from 'moment'

import { Button, DropDown, Modal, PriceComponent, Tooltip } from '@/components'
import Strings from '@/constants'
import { rateAllowsPayLater } from '@/helpers'
import type { RateDataType } from '@/models/property'

import styles from '../styles.scss'

type CancelPropertyDataType = {
  currency: string,
  start: string,
  end: string,
  amount: string,
  nights: string,
  percent: string
}

const formatPolicy = (policy: CancelPropertyDataType) => {
  const { currency, start, end, amount, nights, percent } = policy
  let message = `will result in a ${percent} penalty of the stay charges and fees.`
  if (nights) message = `are subject to a ${nights} night penalty plus taxes and fees.`
  if (amount) message = `will result in a ${amount} ${currency} fee.`
  return (
    <ul key={`${start}-${end}-${amount || nights || percent}`}>
      <li>{Strings.checkout.cancelPolicies.freeCancelation(moment(start).format('LLL ([GMT] Z)'))}</li>
      <li>{Strings.checkout.cancelPolicies.cancelPenalty(moment(start).format('LLL ([GMT] Z)'), message)}</li>
      <li>{Strings.checkout.cancelPolicies.nonRefundableAfter(moment(end).format('LLL ([GMT] Z)'))}</li>
      <li>{Strings.checkout.cancelPolicies.fullPenalty}</li>
    </ul>
  )
}

type RateProps = {
  rate: RateDataType,
  chooseRoom: (rateId: string, checkLink: string) => void
}

const RateComponent = ({rate, chooseRoom}: RateProps) => {
  const [bedGroup, setBedGroup] = React.useState(null)
  const payLater = rateAllowsPayLater(rate)

  React.useEffect(() => {
    if (rate?.bed_groups && Object.values(rate?.bed_groups).length > 0) {
      setBedGroup(Object.values(rate.bed_groups)[0])
    }
  }, [rate?.bed_groups])

  const amenities = rate && rate.amenities
    ? Object.values(rate.amenities)
    : []

  const cancelPolicies: Array<CancelPropertyDataType> = rate && rate.cancel_penalties
    ? rate.cancel_penalties
    : []

  const bedGroupOptions = Object.values(rate?.bed_groups).map(bg => ({
    // $FlowFixMe
    label: bg.description, value: bg
  }))

  const onClick = () => {
    if (!bedGroup) return
    const checkLink = get(bedGroup, 'links.price_check.href')
    chooseRoom(rate.id, checkLink)
  }

  const getOption = (value, options) => {
    const [selection] = options.filter(option => option.value === value)
    return selection
  } 

  return (
    <div className={styles.rateContainer}>
      <DropDown
        options={bedGroupOptions}
        onChange={(option) => setBedGroup(option.value)}
        value={getOption(bedGroup, bedGroupOptions)}
        name="bed-group"
        ariaLabel="bed-group-dropdown"
        placeholder="Bed Groups"
        disabled={bedGroupOptions.length < 2}
      />
      {
        amenities.length > 0 && (
          <div className={styles.amenities}>
            <h4>Amenities</h4>
            <div className={styles.entries}>
              {
                // $FlowFixMe
                amenities.map((amenity: {name: string}) => (
                  <span key={amenity.name}>{amenity.name}</span>
                ))
              }
            </div>
          </div>
        )
      }
      {
        cancelPolicies.length > 0 && (
          <div className={styles.cancellation}>
            <h4>{Strings.cancellations}</h4>
            {cancelPolicies.map((policy) => formatPolicy(policy))}
          </div>
        )
      }
      {payLater && (
        <Tooltip
          content={<p>{Strings.infoCard.payLaterTooltip}</p>}
          minWidth="250px"
        >
        <div className={styles.payLaterTag}>
          {Strings.infoCard.payLater}
        </div>
        </Tooltip>
      )}
      <PriceComponent rate={rate} noHide />
      <Button
        title={Strings.infoCard.chooseRate}
        onClick={onClick}
      />
    </div>
  )
}

type Props = {
  rates: Array<RateDataType>,
  onClose: () => void,
  chooseRoom: (rateId: string, checkLink: string) => void
}

const RatesModal = ({rates, onClose, chooseRoom}: Props) => (
  <Modal
    title="Select rate"
    ariaLabel="Select rate"
    requestClose={onClose}
    visible
  >
    <div className={styles.ratesContainer}>
      {rates.map(rate => <RateComponent rate={rate} chooseRoom={chooseRoom} key={rate.id} />)}
    </div>
  </Modal>
)

export default RatesModal
